import { inProduction } from "../config";
import { inBrowser } from "./utils/env";
import { captureException, setUser } from "@sentry/nextjs";
import { getOrg } from "./utils/site";
import * as amplitude from "@amplitude/analytics-browser";
import { Experiment } from "@amplitude/experiment-js-client";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
const AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY =
  process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY;

if (inBrowser && AMPLITUDE_API_KEY) {
  amplitude.init(AMPLITUDE_API_KEY, {
    defaultTracking: true,
    identityStorage: "localStorage",
    autocapture: {
      attribution: true,
      elementInteractions: true,
      fileDownloads: true,
      formInteractions: true,
      pageViews: false,
      sessions: true,
    },
  });

  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 0.1,
  });
  amplitude.add(sessionReplayTracking);
}

export let experimentClient:
  | ReturnType<typeof Experiment["initializeWithAmplitudeAnalytics"]>
  | undefined;

let experimentInitialized = false;
let experimentLoadFailed = false;

// Initialize experiment client with fetchOnStart disabled for better control
if (inBrowser && AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY) {
  experimentClient = Experiment.initializeWithAmplitudeAnalytics(
    AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY || "",
    {
      fetchOnStart: true,
      automaticExposureTracking: true,
      fetchTimeoutMillis: 3000, // 3 second timeout
    }
  );
}

// Promise that resolves when experiments are loaded
export const isExperimentLoaded = new Promise<void>((resolve) => {
  if (!experimentClient || !inBrowser) {
    // No experiment client, resolve immediately
    experimentLoadFailed = true;
    resolve();
    return;
  }

  // Start and fetch experiments
  experimentClient
    .start()
    .then(() => {
      experimentInitialized = true;
      console.log("Amplitude experiments loaded successfully");
      resolve();
    })
    .catch((error) => {
      console.error("Failed to load Amplitude experiments:", error);
      experimentInitialized = false;
      experimentLoadFailed = true;
      // Resolve anyway to not block the app - users will go to control group
      resolve();
    });
});

/**
 * Check if a user is in the treatment group for an experiment.
 * This function waits for experiments to load before returning the variant.
 * Returns false (control group) if experiments haven't loaded or failed to load.
 *
 * If experiments failed to load once, subsequent calls return immediately without waiting.
 *
 * @param key - The experiment flag key
 * @returns Promise that resolves to true if user is in treatment group, false otherwise
 */
export const getIsExperimentEnabled = async (key: string): Promise<boolean> => {
  // If experiments already failed to load, don't wait again - fail fast
  if (experimentLoadFailed) {
    console.log(
      `Experiment ${key}: previously failed to load, defaulting to control`
    );
    return false;
  }

  // Wait for experiments to load before checking
  try {
    await isExperimentLoaded;
  } catch (error) {
    console.error(
      `Failed to load experiments for ${key}, using control group`,
      error
    );
    return false;
  }

  if (!experimentClient || !experimentInitialized) {
    // Return false (control) when experiment client isn't ready
    // This ensures consistent behavior - users always go to control
    // if experiments haven't loaded yet
    console.log(`Experiment ${key}: not ready, defaulting to control`);
    return false;
  }

  const variant = experimentClient.variant(key);

  // Log for debugging
  console.log(`Experiment ${key}: ${variant.value || "control"}`);

  return variant.value === "treatment";
};

/**
 * Helper to check if experiments are ready synchronously.
 * Useful for debugging or conditional logic.
 */
export const areExperimentsReady = (): boolean => {
  return experimentInitialized;
};

type LogFunction = (event: string, props?: any) => Promise<any>;

export const logEvent: LogFunction = async (event, props) => {
  if (inBrowser && AMPLITUDE_API_KEY) {
    amplitude.track(event, {
      ...(props ? props : {}),
      org: getOrg(),
    });
  } else {
    console.log("TRACK", event, { ...(props ? props : {}), org: getOrg() });
  }
};

export const identify = async (
  id: string | undefined,
  props: any = {}
): Promise<any> => {
  const propsWithOrg = { ...props, org: getOrg() };
  if (inBrowser && inProduction) {
    const hotjar = global.window.hj;

    if (hotjar) {
      hotjar("identify", id || props.resparkClientId, propsWithOrg);
    }
    if (id) {
      amplitude.setUserId(id);
    }
    const identifyObj = new amplitude.Identify();

    Object.keys(propsWithOrg)
      .filter((key) => !!propsWithOrg[key])
      .forEach((key) => {
        identifyObj.set(key, propsWithOrg[key]);
      });

    amplitude.identify(identifyObj);

    try {
      id
        ? setUser({ id, ...propsWithOrg })
        : setUser({ id: propsWithOrg.resparkClientId, ...propsWithOrg });
    } catch (e) {
      captureException(e);
    }
  } else {
    console.log("Identify", id, propsWithOrg);
  }
  return Promise.resolve();
};

type CalendlyEvent = {
  event:
    | "calendly.profile_page_viewed"
    | "calendly.event_type_viewed"
    | "calendly.date_and_time_selected"
    | "calendly.event_scheduled";
  payload: any;
};

function isCalendlyEvent(e: MessageEvent<CalendlyEvent>) {
  return (
    e.origin === "https://calendly.com" &&
    e.data.event &&
    e.data.event.indexOf("calendly.") === 0
  );
}

if (typeof window !== "undefined") {
  window.addEventListener(
    "message",
    function (event: MessageEvent<CalendlyEvent>) {
      if (isCalendlyEvent(event)) {
        logEvent(event.data.event);
      }
    }
  );
}
